import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

import { AppDispatch, RootState } from "../../../redux/store";

/* ⭐ the fresh basket runs on the food cart slice — the same one
   FreshStoreHome's addLine dispatches into. */
import {
  addToCart,
  CartItem,
  CartExtra,
  decrementCartItem,
  removeFromCart,
} from "../../../redux/cartSlice";

/* ⭐ listings come from the food feed, so stock, best-before dates and
   size stock all resolve live while the basket is open. */
import { listenToPublishedFoods } from "../../../redux/foodSlice";


import { nearestDeliveryLocation } from "../../../redux/commercePartnersSlice";
import { updateUserProfile } from "../../../redux/authSlice";

import {
  ArrowLeft, ShoppingBag, Trash2,
  CreditCard, Package, Truck,
  Lock, AlertCircle, X, Check, ChevronRight,
  Mail, Home as HomeIcon, ChevronDown, Gift,
  Smartphone, Store, Sparkles, User, MapPin, PackageX, Search,
  Clock, Leaf, Snowflake,
} from "lucide-react";

import countries, { CountryInfo } from "../../../GLOBAL/Countries";

import {
  addItem, removeItem, addVariant, addExtra,
  setGuestFee, setSmallOrderFee, clearOrder,
  calculateGuestServiceFee, calculateSmallOrderFee,
  type FoodOrderExtra, type RateType,
} from "../../../redux/foodServiceFeeSlice";
/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same set StoreProductCheckout uses,
   with the produce green kept for freshness signals.
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  panel2: "#FFF9EF",

  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",

  green: "#3F8A4C",
  greenSoft: "#E7F3E8",
};

const T = {
  bg: HEART.bg,
  bgSoft: HEART.goldSoft,
  panel2: HEART.panel2,
  ink: HEART.dark,
  muted: HEART.textMuted,
  mutedSoft: HEART.textFaint,
  border: HEART.border,
  divider: HEART.divider,
  gold: HEART.gold,
  goldDark: HEART.goldDark,
  red: HEART.red,
  redDark: HEART.redDark,
  green: HEART.green,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.gold} 100%)`,
};

const GRAD_H = T.gradH;

/* ── fresh checkout backend endpoints ── */
const FRESH_CHECKOUT_URL = "https://europe-west1-eartlas.cloudfunctions.net/createFoodCheckout";
const FRESH_MPESA_CHECKOUT_URL = "https://europe-west1-eartlas.cloudfunctions.net/createFoodMpesaCheckout";
const CREATE_CUSTOMER_URL = "https://europe-west1-eartlas.cloudfunctions.net/createCustomerAccount";

type CreateCustomerResponse = { userId: string };

/* ═══════════════════════════════════════════════════════════
   CART LINE — FreshStoreHome writes composite rows:
     listingId:     "<realId>__<sizeId|base>__<extraId+extraId|none>"
     baseListingId: "<realId>"
     name:          "Tomatoes (1kg crate) + Basil"
     rate:          base + size adjustment + extras, already summed
     portionId?, extraIds?, unit?, netContent?
   Nothing is re-priced here; the row already carries its own total.
═══════════════════════════════════════════════════════════ */
type CartLine = {
  listingId: string;
  baseListingId?: string;
  name: string;
  rate?: number;          // base portion price only when `extras` is present
  price?: number;
  unitPrice?: number;     // base + extras, per serving — what the buyer actually pays per unit
  rateType?: RateType;
  qty: number;
  photo?: string;
  hostId?: string;
  brandId?: string;
  unit?: string;
   colorId?: number; 
  netContent?: number;
  portionId?: string;      // primary portion — sets the price
  portionIds?: string[];   // every portion picked — primary first, rest are extra helpings
  extraIds?: string[];     // menu extras (sauces, toppings) picked
  extras?: CartExtra[];// priced lines for extra helpings + menu extras, per serving
};

const linePrice = (item: CartLine) => item.unitPrice ?? item.rate ?? item.price ?? 0;
/* Cart rows are keyed "<realId>__<portion>__<extras>". When baseListingId
   is missing — older rows, or an addToCart that didn't set it — strip the
   suffix rather than sending the composite key as a listing id. */
const realIdOf = (item: CartLine) =>
  item.baseListingId || String(item.listingId || "").split("__")[0];

/* ── helpers ── */
const fmtPrice = (n: number, sym = "€") =>
  `${sym}${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Sizes arrive as an array from the new seller screen, a keyed map from older docs. */
interface Portion { id: string; name: string; price: number; unit?: string; stock?: number }
const portionsOf = (listing: any): Portion[] => {
  const raw = listing?.portions;
  if (!raw) return [];
  const list: Portion[] = Array.isArray(raw)
    ? raw.map((p: any) => ({
        id: String(p?.id ?? p?.name ?? ""),
        name: p?.name ?? String(p?.id ?? ""),
        price: Number(p?.price) || 0,
        unit: p?.unit ?? "",
        stock: Number(p?.stock) || 0,
      }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({
        id: k,
        name: v?.name ?? k,
        price: Number(v?.price) || 0,
        unit: v?.unit ?? "",
        stock: Number(v?.stock) || 0,
      }));
  return list.filter((p) => p.id);
};

/** Every portion this line carries — primary size first, any extra helpings after. */
const sizeLabel = (item: CartLine, listing: any): string => {
  const ids = item.portionIds?.length ? item.portionIds : (item.portionId ? [item.portionId] : []);
  if (!ids.length) return "";
  const choices = portionsOf(listing);
  const names = ids
    .map((id) => {
      const p = choices.find((x) => x.id === id);
      return p ? (p.unit ? `${p.name} · ${p.unit}` : p.name) : null;
    })
    .filter(Boolean) as string[];
  return names.join(" + ");
};

/** Live stock: the chosen size's own count when there is one, else the item's. */
const stockOf = (item: CartLine, listing: any): number | null => {
  if (!listing) return null;

  if (item.colorId && listing.variants?.[item.colorId]) {
    return Number(listing.variants[item.colorId].stock) || 0;
  }
  
  // Note: If CartItem doesn't have portionId, you might need to add it to CartItem type as well
  if ('portionId' in item && item.portionId) { 
    const p = portionsOf(listing).find((x) => x.id === (item as any).portionId);
    if (p && p.stock != null) return Number(p.stock);
  }
  
  const raw = listing?.stock ?? null;
  if (raw == null) return null;
  const n = Number(raw);
  return isNaN(n) ? null : n;
};

const daysLeft = (bestBefore?: string): number | null => {
  if (!bestBefore) return null;
  const t = Date.parse(bestBefore);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
};

/** Past its best-before date — the grower can't ship it, so it can't be bought. */
const isExpired = (listing: any): boolean => {
  const d = daysLeft(listing?.bestBefore);
  return d != null && d < 0;
};

/** True only when we can positively confirm the line can't be fulfilled. */
const isUnavailable = (item: CartLine, listing: any): boolean => {
  // Listing hasn't arrived yet — don't yank the row, treat as unknown.
  if (!listing) return false;
  if (listing.available === false) return true;
  if (isExpired(listing)) return true;
  const s = stockOf(item, listing);
  return s != null && s <= 0;
};

const unavailableReason = (item: CartLine, listing: any): string => {
  if (isExpired(listing)) return "PAST BEST BEFORE";
  if (listing?.available === false) return "SOLD OUT";
  return "OUT OF STOCK";
};

/** Freshness chip — how long this batch has left. */
const freshChip = (listing: any): { text: string; color: string } | null => {
  const d = daysLeft(listing?.bestBefore);
  if (d === null) {
    const shelf = listing?.shelfLifeDays;
    return shelf ? { text: `${shelf}-day shelf life`, color: T.green } : null;
  }
  if (d <= 0) return { text: "Use today", color: T.red };
  if (d === 1) return { text: "1 day left", color: T.red };
  if (d <= 3) return { text: `${d} days left`, color: T.goldDark };
  return { text: `Good for ${d} days`, color: T.green };
};

const measureLabel = (item: CartLine, listing: any): string => {
  const n = item.netContent ?? listing?.netContent;
  const u = item.unit ?? listing?.unit;
  if (n && u) return `${n} ${u}`;
  if (u) return `per ${u}`;
  return "";
};

const STORAGE_LABEL: Record<string, string> = {
  frozen: "Keep frozen",
  chilled: "Keep chilled",
  ambient: "Room temp",
  dry: "Keep dry",
};

function flagSphere(emoji: string) {
  return (
    <div style={{ position: "relative", width: 20, height: 20, flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%), radial-gradient(circle at 68% 72%, rgba(0,0,0,0.18) 0%, transparent 55%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ width: 20, height: 20, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, lineHeight: 1, background: T.bgSoft, userSelect: "none" }}>{emoji}</div>
    </div>
  );
}

function stripDialCode(raw: string, dialCode: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith(dialCode)) return trimmed.slice(dialCode.length).trim();
  for (const c of countries) {
    if (trimmed.startsWith(c.phoneCode)) return trimmed.slice(c.phoneCode.length).trim();
  }
  return trimmed.replace(/^\+/, "");
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — product-list density, square CTAs.
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,select,input,textarea{font-family:inherit;}
  button{cursor:pointer;}
  .fsc-scope ::-webkit-scrollbar{width:8px;height:8px;}
  .fsc-scope ::-webkit-scrollbar-thumb{background:${T.border};border-radius:8px;}
  .fsc-scope input::placeholder{color:rgba(27,14,6,.34);}
  .fsc-scope :focus-visible{outline:2px solid ${T.red};outline-offset:2px;border-radius:6px;}

  @keyframes fscSpin{to{transform:rotate(360deg);}}
  @keyframes fscGrad{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes fscPop{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}

  .fsc-scope{min-height:100vh;background:${T.bg};font-family:'Montserrat',sans-serif;color:${T.ink};}

  .fsc-card{background:#fff;border:1px solid ${T.border};border-radius:18px;margin-bottom:14px;overflow:hidden;
    box-shadow:0 3px 12px rgba(27,14,6,.05);animation:fscPop .24s ease both;}
  .fsc-head{display:flex;align-items:center;gap:11px;padding:14px 16px;border-bottom:1px solid ${T.divider};user-select:none;transition:background .12s;}
  .fsc-head.tappable{cursor:pointer;}
  .fsc-head.tappable:hover{background:${T.panel2};}
  .fsc-body{padding:16px;}

  .fsc-title{font-size:12.5px;font-weight:800;color:${T.ink};}
  .fsc-sub{font-size:11px;color:${T.muted};margin-top:1px;}

  .fsc-step{width:26px;height:26px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;flex-shrink:0;}
  .fsc-step.done{background:${T.ink};color:#fff;}
  .fsc-step.active{background:${GRAD_H};background-size:200% 100%;animation:fscGrad 4s ease infinite;color:#fff;}
  .fsc-step.pending{background:${T.bgSoft};color:${T.mutedSoft};}

  .fsc-item{display:flex;padding:12px 14px;border:1px solid ${T.border};border-radius:14px;margin-bottom:10px;background:#fff;transition:border-color .15s,box-shadow .15s;}
  .fsc-item:hover{border-color:${T.gold};box-shadow:0 4px 14px rgba(27,14,6,.06);}
  .fsc-item.gone{opacity:.85;border-style:dashed;}
  .fsc-thumb{border-radius:12px;overflow:hidden;position:relative;background:${T.bgSoft};flex-shrink:0;}

  .fsc-ib{width:32px;height:32px;border:1px solid ${T.border};background:#fff;border-radius:10px;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
  .fsc-ib:hover{border-color:${T.gold};background:${T.bgSoft};}
  .fsc-ib.danger:hover{border-color:${T.red};background:${HEART.redSoft};}

  .fsc-check{width:20px;height:20px;border-radius:6px;border:1.5px solid ${T.gold};background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .15s;}
  .fsc-check.on{background:${T.red};border-color:${T.red};}

  .fsc-tag{display:inline-flex;align-items:center;gap:4px;font-size:9.5px;font-weight:800;color:${T.goldDark};background:${T.bgSoft};padding:3px 8px;border-radius:999px;}

  .fsc-qty{display:flex;align-items:center;border:1px solid ${T.border};border-radius:10px;overflow:hidden;}
  .fsc-qty button{width:30px;height:30px;border:none;background:${T.bg};display:flex;align-items:center;justify-content:center;color:${T.red};font-size:15px;font-weight:800;}
  .fsc-qty button:hover{background:${T.panel2};}
  .fsc-qty .n{width:34px;height:30px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:${T.ink};}

  .fsc-fi{width:100%;padding:11px 13px;border:1px solid ${T.border};background:#fff;font-size:13px;color:${T.ink};outline:none;transition:border-color .15s,box-shadow .15s;border-radius:12px;}
  .fsc-fi:focus{border-color:${T.gold};box-shadow:0 0 0 3px ${HEART.goldSoft};}
  .fsc-fi.missing{border-color:${T.red};background:#FFF7F7;}
  .fsc-fl{display:block;font-size:10px;font-weight:800;color:${T.muted};letter-spacing:.09em;text-transform:uppercase;margin-bottom:6px;}

  .fsc-optin{display:flex;align-items:center;gap:11px;padding:13px 14px;border:1px solid ${T.border};border-radius:14px;background:#fff;width:100%;text-align:left;transition:all .15s;}
  .fsc-optin:hover{border-color:${T.gold};background:${T.panel2};}
  .fsc-optin.on.me{border-color:${T.red};background:${HEART.redSoft};}
  .fsc-optin.on.gift{border-color:${T.goldDark};background:${T.bgSoft};}
  .fsc-optin-ic{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .15s;}

  .fsc-cta{width:100%;padding:13px 0;border:none;border-radius:12px;font-size:13px;font-weight:800;letter-spacing:.02em;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .18s;}
  .fsc-cta.go{background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});color:#fff;box-shadow:0 6px 16px rgba(200,32,46,.28);}
  .fsc-cta.go:hover{box-shadow:0 8px 20px rgba(200,32,46,.36);}
  .fsc-cta.go:disabled{opacity:.55;cursor:not-allowed;box-shadow:none;}
  .fsc-cta.wait{background:${T.mutedSoft};color:#fff;cursor:not-allowed;}
  .fsc-cta.mpesa{background:${T.green};color:#fff;box-shadow:0 6px 16px rgba(63,138,76,.25);}
  .fsc-cta.ghost{background:#fff;color:${T.ink};border:1px solid ${T.border};box-shadow:none;}
  .fsc-cta.ghost:hover{background:${T.bgSoft};}

  .fsc-pay-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
  .fsc-pay{border:1.5px solid ${T.border};background:#fff;border-radius:14px;padding:14px 10px;display:flex;flex-direction:column;align-items:center;gap:7px;transition:all .18s;position:relative;overflow:hidden;user-select:none;}
  .fsc-pay:hover{border-color:${T.gold};background:${T.bgSoft};}
  .fsc-pay.card.on{border-color:${T.red};background:${HEART.redSoft};}
  .fsc-pay.mpesa.on{border-color:${T.green};background:rgba(63,138,76,.06);}
  .fsc-pay-check{position:absolute;top:8px;right:8px;width:17px;height:17px;border-radius:50%;border:1.5px solid ${T.border};background:#fff;display:flex;align-items:center;justify-content:center;transition:all .15s;}
  .fsc-pay.card.on .fsc-pay-check{background:${T.red};border-color:${T.red};}
  .fsc-pay.mpesa.on .fsc-pay-check{background:${T.green};border-color:${T.green};}
  .fsc-pay-label{font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:${T.ink};}
  .fsc-pay-sub{font-size:9px;color:${T.muted};}

  .fsc-rate{border:1px solid ${T.border};padding:12px 14px;display:flex;align-items:center;gap:11px;transition:border-color .15s,background .15s;background:#fff;border-radius:12px;margin-bottom:8px;cursor:pointer;}
  .fsc-rate:hover{border-color:${T.gold};background:${T.bgSoft};}
  .fsc-rate.on{border-color:${T.red};background:${HEART.redSoft};}

  .fsc-dd{position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:500;background:#fff;border:1px solid ${T.border};border-radius:14px;max-height:230px;overflow-y:auto;box-shadow:0 12px 30px rgba(27,14,6,.15);}
  .fsc-opt{display:flex;align-items:center;gap:10px;padding:10px 13px;font-size:12px;color:${T.ink};transition:background .12s;border-bottom:1px solid ${T.divider};cursor:pointer;}
  .fsc-opt:hover{background:${T.panel2};}

  .fsc-bc{font-size:11px;font-weight:700;color:${T.mutedSoft};letter-spacing:.08em;text-transform:capitalize;}
  .fsc-bc.on{color:${T.red};font-weight:900;}
  .fsc-bc-sep{color:${T.mutedSoft};font-size:10px;margin:0 6px;}

  @media (prefers-reduced-motion: reduce){
    .fsc-cta.go,.fsc-step.active{animation:none !important;}
  }
`;

/* ── shared bits ── */
const FeeRow: React.FC<{
  label: string; value: string; sub?: string; bold?: boolean; muted?: boolean; green?: boolean; red?: boolean; loading?: boolean;
}> = ({ label, value, sub, bold, muted, green, red, loading }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${T.divider}` }}>
    <div>
      <div style={{ fontSize: bold ? 14 : 12.5, fontWeight: bold ? 800 : 500, color: muted ? T.muted : T.ink }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{sub}</div>}
    </div>
    {loading
      ? <span style={{ fontSize: 11, color: T.muted }}>Working it out…</span>
      : <span style={{ fontSize: bold ? 16 : 13, fontWeight: bold ? 900 : 600, color: red ? T.red : green ? T.green : T.ink }}>{value}</span>}
  </div>
);

const StepBadge: React.FC<{ n: number; current: number }> = React.memo(({ n, current }) => (
  <div className={`fsc-step ${n < current ? "done" : n === current ? "active" : "pending"}`}>
    {n < current ? <Check size={13} color="#fff" /> : n}
  </div>
));

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="fsc-fl">{label}</label>{children}</div>
);

/* ═══════════════════════════════════════════════════════════
   BASKET ROW — size and extras come pre-baked into the line's
   name and rate, so this only surfaces what's fresh-specific:
   the measure, the batch's remaining life, and low stock.
═══════════════════════════════════════════════════════════ */
const ItemRow: React.FC<{
  item: CartLine; listing: any; sym: string;
  checked: boolean; onCheck: () => void;
  onInc: () => void; onDec: () => void; onRemove: () => void; onNavigate: () => void;
  stock: number | null;
}> = React.memo(({ item, listing, sym, checked, onCheck, onInc, onDec, onRemove, onNavigate, stock }) => {
  const previewImg = listing?.photos?.[0] ?? item.photo;
  const growerName = listing?.brandName ?? listing?.vendor ?? "Grower";
  const lowStock = stock != null && stock > 0 && stock <= 5;
  const fresh = freshChip(listing);
  const measure = measureLabel(item, listing);
  const size = sizeLabel(item, listing);
  const storage = STORAGE_LABEL[String(listing?.storage ?? "").toLowerCase()];

  return (
    <div className="fsc-item">
      <div style={{ marginTop: 2, marginRight: 12 }}>
        <div className={`fsc-check${checked ? " on" : ""}`} onClick={onCheck}>
          {checked && <Check size={12} color="#fff" />}
        </div>
      </div>
      <div onClick={onNavigate} className="fsc-thumb" style={{ width: 74, height: 90, cursor: "pointer", marginRight: 12 }}>
        {previewImg
          ? <img src={previewImg} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={22} color={T.mutedSoft} /></div>}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Store size={11} color={T.red} />
          <span style={{ fontSize: 11, fontWeight: 800, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{growerName}</span>
          {listing?.isOrganic && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 800, color: T.green, background: HEART.greenSoft, padding: "2px 7px", borderRadius: 999 }}>
              <Leaf size={9} /> Organic
            </span>
          )}
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.35, cursor: "pointer", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }} onClick={onNavigate}>
          {item.name}
        </div>

        {(size || measure || storage) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {size && <span className="fsc-tag">{size}</span>}
            {measure && <span className="fsc-tag">{measure}</span>}
            {storage && <span className="fsc-tag"><Snowflake size={9} /> {storage}</span>}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {fresh && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 800, color: fresh.color }}>
              <Clock size={10} /> {fresh.text}
            </span>
          )}
          {lowStock && <span style={{ fontSize: 10.5, fontWeight: 700, color: T.goldDark }}>Only {stock} left</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", paddingTop: 6 }}>
          <div className="fsc-qty">
            <button onClick={onDec} aria-label="Decrease quantity">−</button>
            <div className="n">{item.qty}</div>
            <button onClick={onInc} aria-label="Increase quantity" disabled={stock != null && item.qty >= stock}
              style={stock != null && item.qty >= stock ? { opacity: .4, cursor: "not-allowed" } : undefined}>+</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: T.ink }}>{fmtPrice(item.qty * linePrice(item), sym)}</div>
              {item.qty > 1 && <div style={{ fontSize: 10, color: T.muted }}>{fmtPrice(linePrice(item), sym)} each</div>}
            </div>
            <button className="fsc-ib danger" onClick={onRemove} aria-label="Remove item"><Trash2 size={13} color={T.red} /></button>
          </div>
        </div>
      </div>
    </div>
  );
});

/* Unavailable read-only row → browse the grower instead */
const UnavailableRow: React.FC<{ item: CartLine; listing: any; sym: string; reason: string; onRemove: () => void; onSimilar: () => void; }> =
  ({ item, listing, sym, reason, onRemove, onSimilar }) => {
    const previewImg = listing?.photos?.[0] ?? item.photo;
    return (
      <div className="fsc-item gone">
        <div className="fsc-thumb" style={{ width: 66, height: 82, marginRight: 12, position: "relative" }}>
          {previewImg
            ? <img src={previewImg} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(0.5)" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={20} color={T.mutedSoft} /></div>}
          <div style={{ position: "absolute", inset: 0, background: "rgba(27,14,6,.28)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PackageX size={20} color="#fff" />
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>{item.name}</div>
          <span style={{ fontSize: 10.5, fontWeight: 800, color: T.red, letterSpacing: ".04em" }}>{reason}</span>
          <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 6, flexWrap: "wrap" as const }}>
            <button className="fsc-cta ghost" style={{ width: "auto", padding: "7px 12px", fontSize: 11.5 }} onClick={onSimilar}>
              <Search size={12} /> Find similar
            </button>
            <button className="fsc-ib danger" onClick={onRemove} aria-label="Remove" style={{ width: 32 }}><Trash2 size={13} color={T.red} /></button>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.mutedSoft, textDecoration: "line-through" }}>{fmtPrice(linePrice(item), sym)}</div>
        </div>
      </div>
    );
  };

/* ═══════════════════════════════════════════════════════════
   COUNTRY / DIAL PICKERS
═══════════════════════════════════════════════════════════ */
const CountryField: React.FC<{ value: string; onChange: (c: CountryInfo) => void }> = React.memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.name === value);
  const filtered = useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", border: `1px solid ${open ? T.gold : T.border}`, background: "#fff", cursor: "pointer", borderRadius: 12, boxShadow: open ? `0 0 0 3px ${HEART.goldSoft}` : "none" }}>
        {selected ? <>{flagSphere(selected.emoji)}<span style={{ fontSize: 13, color: T.ink, flex: 1 }}>{selected.name}</span></> : <span style={{ fontSize: 13, color: T.mutedSoft, flex: 1 }}>Pick a country</span>}
        <ChevronDown size={13} color={T.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="fsc-dd" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "9px 11px", borderBottom: `1px solid ${T.divider}`, position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search country…" value={query} onChange={e => setQuery(e.target.value)} className="fsc-fi" style={{ borderRadius: 10, fontSize: 12, padding: "8px 11px" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="fsc-opt" onClick={() => { onChange(c); setOpen(false); setQuery(""); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11 }}>{c.phoneCode}</span>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: "14px 12px", fontSize: 12, color: T.muted }}>No matches — try another spelling.</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const DialField: React.FC<{ dialCode: string; onChange: (code: string) => void }> = React.memo(({ dialCode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.phoneCode === dialCode);
  const filtered = useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 12px", border: `1px solid ${T.border}`, borderRight: "none", background: T.bgSoft, cursor: "pointer", minWidth: 92, borderRadius: "12px 0 0 12px" }}>
        {selected && flagSphere(selected.emoji)}
        <span style={{ fontSize: 12, color: T.ink }}>{dialCode}</span>
        <ChevronDown size={11} color={T.muted} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="fsc-dd" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }} style={{ width: 230, right: "auto" }}>
            <div style={{ padding: "8px 9px", borderBottom: `1px solid ${T.divider}`, position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} className="fsc-fi" style={{ borderRadius: 10, fontSize: 11, padding: "7px 9px" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="fsc-opt" onClick={() => { onChange(c.phoneCode); setOpen(false); setQuery(""); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1, fontSize: 11 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11, fontWeight: 700 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */
type AddrState = {
  firstName: string; lastName: string; email: string;
  country: string; state: string; city: string; postalCode: string;
  address: string; dialCode: string; phone: string;
};
type PaymentMethod = "card" | "mpesa";
type RecipientState = AddrState;

const emptyAddr = (): AddrState => ({
  firstName: "", lastName: "", email: "",
  country: "", state: "", city: "", postalCode: "",
  address: "", dialCode: "+254", phone: "",
});

const ACTIVE_STEPS = ["basket", "delivery", "payment"] as const;
type StepKey = typeof ACTIVE_STEPS[number];

/* ═══════════════════════════════════════════════════════════
   STEP 1: BASKET
═══════════════════════════════════════════════════════════ */
const BasketStep: React.FC<{
  items: CartLine[]; goneItems: CartLine[]; getListing: (id: string) => any; stepIdx: number; stepNumber: number; sym: string;
  checkedItems: Set<string>;
  onToggleAll: () => void; onToggleItem: (key: string) => void; onContinue: () => void;
  onInc: (i: CartLine) => void; onDec: (i: CartLine) => void; onRemove: (i: CartLine) => void; onNavigate: (i: CartLine) => void;
  onSimilar: (i: CartLine) => void;
}> = React.memo(({ items, goneItems, getListing, stepIdx, stepNumber, sym, checkedItems, onToggleAll, onToggleItem, onContinue, onInc, onDec, onRemove, onNavigate, onSimilar }) => {
  const allChecked = items.length > 0 && items.every(i => checkedItems.has(i.listingId));
  const someChecked = items.some(i => checkedItems.has(i.listingId));
  const checkedSubtotal = items.filter(i => checkedItems.has(i.listingId)).reduce((s, i) => s + i.qty * linePrice(i), 0);

  return (
    <>
      <div className="fsc-card">
        <div className="fsc-head">
          <StepBadge n={stepNumber} current={stepIdx + 1} />
          <div>
            <div className="fsc-title">Your basket</div>
            <div className="fsc-sub">{items.length} available item{items.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
        {stepIdx === 0 && (
          <div className="fsc-body">
            {items.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", background: T.panel2, borderRadius: 12, marginBottom: 12 }}>
                <div className={`fsc-check${allChecked ? " on" : ""}`} onClick={onToggleAll}>
                  {allChecked && <Check size={11} color="#fff" />}
                  {!allChecked && someChecked && <div style={{ width: 8, height: 2, background: T.gold, borderRadius: 1 }} />}
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>Select all ({items.length})</span>
                {someChecked && (
                  <span style={{ marginLeft: "auto", fontSize: 11, color: T.red, fontWeight: 700 }}>
                    {items.filter(i => checkedItems.has(i.listingId)).length} chosen · {fmtPrice(checkedSubtotal, sym)}
                  </span>
                )}
              </div>
            )}

            {items.map((item) => {
              const listing = getListing(realIdOf(item));
              return (
                <ItemRow key={item.listingId} item={item} listing={listing} sym={sym}
                  checked={checkedItems.has(item.listingId)} onCheck={() => onToggleItem(item.listingId)}
                  onInc={() => onInc(item)} onDec={() => onDec(item)}
                  onRemove={() => onRemove(item)} onNavigate={() => onNavigate(item)}
                  stock={stockOf(item, listing)} />
              );
            })}

            {items.length === 0 && (
              <div style={{ textAlign: "center", padding: "26px 12px", color: T.muted, fontSize: 13 }}>
                Nothing available to check out right now.
              </div>
            )}

            <button onClick={onContinue} className={`fsc-cta ${someChecked ? "go" : "wait"}`} disabled={!someChecked} style={{ marginTop: 4 }}>
              Continue to delivery <ChevronRight size={14} />
            </button>
          </div>
        )}
        {stepIdx > 0 && (
          <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const }}>
            {items.filter(i => checkedItems.has(i.listingId)).slice(0, 4).map(item => {
              const img = getListing(realIdOf(item))?.photos?.[0] ?? item.photo;
              return (
                <div key={item.listingId} className="fsc-thumb" style={{ width: 40, height: 50 }}>
                  {img && <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
              );
            })}
            {items.filter(i => checkedItems.has(i.listingId)).length > 4 && (
              <span style={{ fontSize: 11, color: T.muted }}>+{items.filter(i => checkedItems.has(i.listingId)).length - 4} more</span>
            )}
          </div>
        )}
      </div>

      {/* Can't be fulfilled — sold out, no stock, or past its best-before */}
      {goneItems.length > 0 && (
        <div className="fsc-card">
          <div className="fsc-head">
            <div className="fsc-optin-ic" style={{ background: HEART.redSoft, width: 26, height: 26 }}>
              <PackageX size={14} color={T.red} />
            </div>
            <div>
              <div className="fsc-title">Not available</div>
              <div className="fsc-sub">{goneItems.length} item{goneItems.length !== 1 ? "s" : ""} moved here — browse similar</div>
            </div>
          </div>
          <div className="fsc-body" style={{ maxHeight: 320, overflowY: "auto" }}>
            {goneItems.map((item) => {
              const listing = getListing(realIdOf(item));
              return (
                <UnavailableRow key={item.listingId} item={item} listing={listing} sym={sym}
                  reason={unavailableReason(item, listing)}
                  onRemove={() => onRemove(item)} onSimilar={() => onSimilar(item)} />
              );
            })}
          </div>
        </div>
      )}
    </>
  );
});

/* ═══════════════════════════════════════════════════════════
   Reusable RECIPIENT FORM (gift block)
═══════════════════════════════════════════════════════════ */
const RecipientForm: React.FC<{
  value: RecipientState;
  onChange: (patch: Partial<RecipientState>) => void;
}> = ({ value, onChange }) => (
  <div style={{ marginTop: 12, padding: 14, background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 14 }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
      <Field label="Their first name"><input className="fsc-fi" placeholder="First name" defaultValue={value.firstName} onBlur={e => onChange({ firstName: e.target.value })} /></Field>
      <Field label="Their last name"><input className="fsc-fi" placeholder="Last name" defaultValue={value.lastName} onBlur={e => onChange({ lastName: e.target.value })} /></Field>
    </div>
    <div style={{ marginBottom: 10 }}>
      <Field label="Their email">
        <div style={{ position: "relative" }}>
          <input className="fsc-fi" placeholder="them@email.com" defaultValue={value.email} onBlur={e => onChange({ email: e.target.value })} style={{ paddingLeft: 36 }} />
          <Mail size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
        </div>
      </Field>
      <p style={{ fontSize: 10, color: T.muted, marginTop: 6 }}>We'll set up an account with this email so they can track the delivery.</p>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
      <Field label="Country"><CountryField value={value.country} onChange={c => onChange({ country: c.name, dialCode: c.phoneCode })} /></Field>
      <Field label="State / Province"><input className="fsc-fi" placeholder="e.g. Nairobi" defaultValue={value.state} onBlur={e => onChange({ state: e.target.value })} /></Field>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
      <Field label="City"><input className="fsc-fi" placeholder="City" defaultValue={value.city} onBlur={e => onChange({ city: e.target.value })} /></Field>
      <Field label="Postal code"><input className="fsc-fi" placeholder="e.g. 00100" defaultValue={value.postalCode} onBlur={e => onChange({ postalCode: e.target.value })} /></Field>
    </div>
    <div style={{ marginBottom: 10 }}>
      <Field label="Delivery address">
        <div style={{ position: "relative" }}>
          <input className="fsc-fi" placeholder="Street, apartment, building" defaultValue={value.address} onBlur={e => onChange({ address: e.target.value })} style={{ paddingLeft: 36 }} />
          <HomeIcon size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
        </div>
      </Field>
    </div>
    <Field label="Their WhatsApp number">
      <div style={{ display: "flex" }}>
        <DialField dialCode={value.dialCode} onChange={code => onChange({ dialCode: code })} />
        <input className="fsc-fi" placeholder="7001234567" defaultValue={stripDialCode(value.phone, value.dialCode)} onBlur={e => onChange({ phone: stripDialCode(e.target.value, value.dialCode) })} style={{ flex: 1, borderRadius: "0 12px 12px 0" }} inputMode="tel" />
      </div>
    </Field>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   STEP 2: DELIVERY
═══════════════════════════════════════════════════════════ */
const DeliveryStep: React.FC<{
  stepIdx: number; myIdx: number; stepNumber: number;
  useMyself: boolean; onToggleMyself: () => void;
  myself: AddrState; onPatchMyself: (p: Partial<AddrState>) => void; missing: Record<string, boolean>;
  giftSomeone: boolean; onToggleGift: () => void; gift: RecipientState; onPatchGift: (p: Partial<RecipientState>) => void;
  onContinue: () => void; onBack: () => void; saving: boolean;
}> = React.memo(({
  stepIdx, myIdx, stepNumber,
  useMyself, onToggleMyself, myself, onPatchMyself, missing,
  giftSomeone, onToggleGift, gift, onPatchGift,
  onContinue, onBack, saving,
}) => {
  /* Local refs for the "Myself" block so typing doesn't re-render the tree. */
  const rFirst = useRef<HTMLInputElement>(null), rLast = useRef<HTMLInputElement>(null), rEmail = useRef<HTMLInputElement>(null);
  const rState = useRef<HTMLInputElement>(null), rCity = useRef<HTMLInputElement>(null), rPostal = useRef<HTMLInputElement>(null);
  const rAddress = useRef<HTMLInputElement>(null), rPhone = useRef<HTMLInputElement>(null);
  const [country, setCountry] = useState(myself.country);
  const [dialCode, setDialCode] = useState(myself.dialCode);

  useEffect(() => { setCountry(myself.country); setDialCode(myself.dialCode); }, [myself.country, myself.dialCode]);

  const blurField = (key: keyof AddrState) => (e: React.FocusEvent<HTMLInputElement>) => onPatchMyself({ [key]: e.target.value });
  const blurPhone = (e: React.FocusEvent<HTMLInputElement>) => { const clean = stripDialCode(e.target.value, dialCode); if (e.target.value !== clean) e.target.value = clean; onPatchMyself({ phone: clean }); };

  const canContinue =
    (useMyself && !!(rFirst.current?.value || myself.firstName) && !!(rPhone.current?.value || myself.phone)) ||
    (giftSomeone && !!gift.firstName && !!gift.email);

  const flushMyself = () => {
    onPatchMyself({
      firstName: rFirst.current?.value ?? myself.firstName,
      lastName: rLast.current?.value ?? myself.lastName,
      email: rEmail.current?.value ?? myself.email,
      state: rState.current?.value ?? myself.state,
      city: rCity.current?.value ?? myself.city,
      postalCode: rPostal.current?.value ?? myself.postalCode,
      address: rAddress.current?.value ?? myself.address,
      phone: stripDialCode(rPhone.current?.value ?? myself.phone, dialCode),
      country, dialCode,
    });
  };

  const handleContinue = () => { if (useMyself) flushMyself(); onContinue(); };

  const summary = (() => {
    if (giftSomeone) return `Gift → ${gift.firstName || "recipient"}${gift.city ? ` · ${gift.city}` : ""}`;
    if (useMyself) return `${myself.firstName} ${myself.lastName} · ${myself.city || myself.country || "address on file"}`;
    return "No option selected";
  })();

  return (
    <div className="fsc-card">
      <div className={`fsc-head${stepIdx > myIdx ? " tappable" : ""}`} onClick={stepIdx > myIdx ? onBack : undefined}>
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <div>
          <div className="fsc-title">Where it's going</div>
          {stepIdx > myIdx && <div className="fsc-sub">{summary}</div>}
        </div>
        {stepIdx > myIdx && <Check size={15} color={T.green} style={{ marginLeft: "auto" }} />}
      </div>

      {stepIdx === myIdx && (
        <div className="fsc-body">
          {/* ── OPTION 1: Myself ── */}
          <button className={`fsc-optin me${useMyself ? " on" : ""}`} onClick={onToggleMyself} style={{ marginBottom: 10 }}>
            <div className={`fsc-check${useMyself ? " on" : ""}`}>{useMyself && <Check size={11} color="#fff" />}</div>
            <div className="fsc-optin-ic" style={{ background: useMyself ? T.red : HEART.redSoft }}>
              <User size={16} color={useMyself ? "#fff" : T.red} />
            </div>
            <div>
              <p style={{ fontSize: 12.5, fontWeight: 800, color: T.ink }}>Myself</p>
              <p style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>Deliver to me — fill in anything we're missing</p>
            </div>
          </button>

          <AnimatePresence>
            {useMyself && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: .22 }} style={{ overflow: "hidden" }}>
                <div style={{ padding: 14, background: HEART.redSoft, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 12 }}>
                  {Object.values(missing).some(Boolean) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: `1px solid ${T.border}`, padding: "9px 12px", marginBottom: 12, borderRadius: 12 }}>
                      <AlertCircle size={13} color={T.red} />
                      <p style={{ fontSize: 11, color: T.ink }}>We're missing a few details — fill the highlighted fields and we'll save them to your profile.</p>
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="First name"><input ref={rFirst} className={`fsc-fi${missing.firstName ? " missing" : ""}`} placeholder="e.g. Alex" defaultValue={myself.firstName} onBlur={blurField("firstName")} /></Field>
                    <Field label="Last name"><input ref={rLast} className="fsc-fi" placeholder="e.g. Smith" defaultValue={myself.lastName} onBlur={blurField("lastName")} /></Field>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Email">
                      <div style={{ position: "relative" }}>
                        <input ref={rEmail} className={`fsc-fi${missing.email ? " missing" : ""}`} placeholder="you@email.com" defaultValue={myself.email} onBlur={blurField("email")} style={{ paddingLeft: 36 }} />
                        <Mail size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
                      </div>
                    </Field>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="Country"><CountryField value={country} onChange={(c) => { setCountry(c.name); setDialCode(c.phoneCode); onPatchMyself({ country: c.name, dialCode: c.phoneCode }); }} /></Field>
                    <Field label="State / Province"><input ref={rState} className="fsc-fi" placeholder="e.g. Nairobi" defaultValue={myself.state} onBlur={blurField("state")} /></Field>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="City"><input ref={rCity} className={`fsc-fi${missing.city ? " missing" : ""}`} placeholder="City" defaultValue={myself.city} onBlur={blurField("city")} /></Field>
                    <Field label="Postal code"><input ref={rPostal} className="fsc-fi" placeholder="e.g. 00100" defaultValue={myself.postalCode} onBlur={blurField("postalCode")} /></Field>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Delivery address">
                      <div style={{ position: "relative" }}>
                        <input ref={rAddress} className={`fsc-fi${missing.address ? " missing" : ""}`} placeholder="Street, apartment, building" defaultValue={myself.address} onBlur={blurField("address")} style={{ paddingLeft: 36 }} />
                        <HomeIcon size={13} color={T.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
                      </div>
                    </Field>
                  </div>
                  <Field label="WhatsApp number">
                    <div style={{ display: "flex" }}>
                      <DialField dialCode={dialCode} onChange={(v) => { setDialCode(v); onPatchMyself({ dialCode: v }); }} />
                      <input ref={rPhone} className={`fsc-fi${missing.phone ? " missing" : ""}`} placeholder="7001234567" defaultValue={stripDialCode(myself.phone, myself.dialCode)} onBlur={blurPhone} style={{ flex: 1, borderRadius: "0 12px 12px 0" }} inputMode="tel" />
                    </div>
                    <p style={{ fontSize: 10, color: T.muted, marginTop: 6 }}>We add {dialCode} for you — just the number.</p>
                  </Field>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── OPTION 2: Gift someone ── */}
          <button className={`fsc-optin gift${giftSomeone ? " on" : ""}`} onClick={onToggleGift}>
            <div className={`fsc-check${giftSomeone ? " on" : ""}`} style={giftSomeone ? { background: T.goldDark, borderColor: T.goldDark } : undefined}>{giftSomeone && <Check size={11} color="#fff" />}</div>
            <div className="fsc-optin-ic" style={{ background: giftSomeone ? T.goldDark : T.bgSoft }}>
              <Gift size={16} color={giftSomeone ? "#fff" : T.goldDark} />
            </div>
            <div>
              <p style={{ fontSize: 12.5, fontWeight: 800, color: T.ink }}>Gift someone</p>
              <p style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>Deliver to them — we'll create their account</p>
            </div>
          </button>
          <AnimatePresence>
            {giftSomeone && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: .22 }} style={{ overflow: "hidden" }}>
                <RecipientForm value={gift} onChange={onPatchGift} />
              </motion.div>
            )}
          </AnimatePresence>

          <button onClick={handleContinue} className={`fsc-cta ${canContinue && !saving ? "go" : "wait"}`} disabled={!canContinue || saving} style={{ marginTop: 16 }}>
            {saving ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "fscSpin 1s linear infinite" }} /> : null}
            {saving ? "Saving…" : <>Continue to payment <ChevronRight size={14} /></>}
          </button>
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 3: DELIVERY RATES + PAYMENT (per-grower zone)
═══════════════════════════════════════════════════════════ */
const PaymentStep: React.FC<{
  stepIdx: number; myIdx: number; stepNumber: number; addr: AddrState;
  subtotal: number; guestsFee: number; smallOrderFee: number; sym: string; grandTotal: number; feeLoading: boolean;
  canPay: boolean; payingCard: boolean; payingMpesa: boolean; payError: string | null;
  onPayCard: () => void; onPayMpesa: () => void; onDismissError: () => void;
  selectedPayment: PaymentMethod; onSelectPayment: (p: PaymentMethod) => void;
  brandRates: Record<string, any[]>; brandLoading: Record<string, boolean>; brandErrors: Record<string, string | null>;
  brandZones: Record<string, any>; selectedBrandRates: Record<string, any>; onSelectBrandRate: (b: string, r: any) => void; refetchRates: () => void;
  checkedBrandIds: string[]; brandName: (id: string) => string;
}> = React.memo(({
  stepIdx, myIdx, stepNumber, addr, subtotal, guestsFee, smallOrderFee, sym, grandTotal, feeLoading,
  canPay, payingCard, payingMpesa, payError, onPayCard, onPayMpesa, onDismissError,
  selectedPayment, onSelectPayment,
  brandRates, brandLoading, brandErrors, brandZones, selectedBrandRates, onSelectBrandRate, refetchRates,
  checkedBrandIds, brandName,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const fbsTotal = checkedBrandIds.reduce((sum, bid) => { const r = selectedBrandRates[bid]; return sum + (r ? (r.totalPrice ?? r.price ?? 0) : 0); }, 0);
  const isActive = stepIdx === myIdx;

  return (
    <div className="fsc-card">
      <div className="fsc-head tappable" onClick={() => isActive && setCollapsed(v => !v)}>
        <StepBadge n={stepNumber} current={stepIdx + 1} />
        <div>
          <div className="fsc-title">Delivery & payment</div>
          {isActive && collapsed && <div className="fsc-sub">{fbsTotal > 0 ? `Delivery ${fmtPrice(fbsTotal, sym)}` : "Tap to choose delivery"}</div>}
        </div>
        {isActive && <ChevronDown size={15} color={T.muted} style={{ marginLeft: "auto", transition: "transform .2s", transform: collapsed ? "rotate(0)" : "rotate(180deg)" }} />}
      </div>

      <AnimatePresence initial={false}>
        {isActive && !collapsed && (
          <motion.div key="pay-body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .24, ease: [.4, 0, .2, 1] }} style={{ overflow: "hidden" }}>
            <div className="fsc-body">
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14, padding: "11px 13px", background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: GRAD_H, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Truck size={15} color="#fff" />
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Delivered by each grower</p>
                  <p style={{ fontSize: 11, color: T.muted }}>Rates to {addr.city || addr.country || "your address"}, per grower</p>
                </div>
              </div>

              {checkedBrandIds.map(bid => {
                const rates = brandRates[bid] ?? [];
                const loading = brandLoading[bid] ?? false;
                const error = brandErrors[bid] ?? null;
                const selRate = selectedBrandRates[bid];
                const zone = brandZones[bid];
                return (
                  <div key={bid} style={{ marginBottom: 12, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "9px 13px", background: T.bg, borderBottom: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 8 }}>
                      <Store size={12} color={T.red} />
                      <span style={{ fontSize: 11, fontWeight: 800, color: T.ink }}>{brandName(bid)}</span>
                      {zone?.name && (
                        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: T.goldDark, background: T.bgSoft, padding: "2px 8px", borderRadius: 999 }}>
                          <MapPin size={9} /> {zone.name}
                        </span>
                      )}
                    </div>
                    <div style={{ padding: "10px 13px" }}>
                      {loading && <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}><span style={{ width: 13, height: 13, border: `2px solid ${T.border}`, borderTopColor: T.gold, borderRadius: "50%", animation: "fscSpin 1s linear infinite", display: "inline-block" }} /><span style={{ fontSize: 11, color: T.muted }}>Checking rates…</span></div>}
                      {error && !loading && <div style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={12} color={T.red} /><span style={{ fontSize: 11, color: T.red, flex: 1 }}>{error}</span><button onClick={refetchRates} style={{ fontSize: 10, color: T.red, background: "none", border: "none", textDecoration: "underline" }}>Try again</button></div>}
                      {!loading && !error && rates.length === 0 && <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}><Check size={12} color={T.green} /><span style={{ fontSize: 11, color: T.green }}>Free delivery from this grower</span></div>}
                      {!loading && rates.map((rate: any, i: number) => {
                        const isSel = selRate?.productCode === rate.productCode;
                        const price = rate.totalPrice ?? rate.price ?? 0;
                        return (
                          <div key={i} className={`fsc-rate${isSel ? " on" : ""}`} onClick={() => onSelectBrandRate(bid, rate)}>
                            <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSel ? T.red : T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {isSel && <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.red }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{rate.productName ?? "Grower delivery"}</p>
                              <p style={{ fontSize: 10, color: T.muted }}>{rate.deliveryTime ?? "Est. delivery shown at dispatch"}</p>
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 800, color: price === 0 ? T.green : T.ink }}>{price === 0 ? "Free" : fmtPrice(price, sym)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* totals */}
              <div style={{ background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 12, padding: "13px 15px", marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                  <span style={{ fontSize: 12, color: T.muted }}>Items</span><span style={{ fontSize: 12, fontWeight: 700 }}>{fmtPrice(subtotal, sym)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                  <span style={{ fontSize: 12, color: T.muted }}>Service fee</span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{feeLoading ? "Working it out…" : fmtPrice(guestsFee, sym)}</span>
                </div>
                {smallOrderFee > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                    <span style={{ fontSize: 12, color: T.muted }}>Small order fee</span><span style={{ fontSize: 12, fontWeight: 700 }}>{fmtPrice(smallOrderFee, sym)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                  <span style={{ fontSize: 12, color: T.muted }}>Delivery</span><span style={{ fontSize: 12, fontWeight: 700 }}>{fmtPrice(fbsTotal, sym)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0 2px" }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: T.red }}>{fmtPrice(grandTotal, sym)}</span>
                </div>
              </div>

              {/* payment picker */}
              <p style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: ".12em", textTransform: "uppercase" as const, marginBottom: 10 }}>How you'll pay</p>
              <div className="fsc-pay-grid">
                <div className={`fsc-pay card${selectedPayment === "card" ? " on" : ""}`} onClick={() => onSelectPayment("card")}>
                  <div className="fsc-pay-check">{selectedPayment === "card" && <Check size={9} color="#fff" />}</div>
                  <CreditCard size={24} color={selectedPayment === "card" ? T.red : T.muted} />
                  <span className="fsc-pay-label" style={{ color: selectedPayment === "card" ? T.red : undefined }}>Card</span>
                  <span className="fsc-pay-sub">Debit &amp; credit</span>
                </div>
                <div className={`fsc-pay mpesa${selectedPayment === "mpesa" ? " on" : ""}`} onClick={() => onSelectPayment("mpesa")}>
                  <div className="fsc-pay-check">{selectedPayment === "mpesa" && <Check size={9} color="#fff" />}</div>
                  <Smartphone size={24} color={selectedPayment === "mpesa" ? T.green : T.muted} />
                  <span className="fsc-pay-label" style={{ color: selectedPayment === "mpesa" ? T.green : undefined }}>M-Pesa</span>
                  <span className="fsc-pay-sub">Mobile money</span>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {selectedPayment === "card" ? (
                  <motion.button key="card" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
                    onClick={onPayCard} disabled={!canPay} className={`fsc-cta ${canPay ? "go" : "wait"}`}>
                    {payingCard ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "fscSpin 1s linear infinite" }} /> : <Lock size={13} />}
                    {payingCard ? "Taking you to checkout…" : `Pay ${fmtPrice(grandTotal, sym)}`}
                  </motion.button>
                ) : (
                  <motion.button key="mpesa" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
                    onClick={onPayMpesa} disabled={!canPay} className={`fsc-cta ${canPay ? "mpesa" : "wait"}`}>
                    {payingMpesa ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "fscSpin 1s linear infinite" }} /> : <Smartphone size={13} />}
                    {payingMpesa ? "Connecting to M-Pesa…" : `Pay with M-Pesa · ${fmtPrice(grandTotal, sym)}`}
                  </motion.button>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {payError && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, background: HEART.redSoft, border: "1px solid rgba(200,32,46,.25)", padding: "12px 14px", marginTop: 10, borderRadius: 12 }}>
                    <AlertCircle size={13} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 11, color: T.red, flex: 1 }}>{payError}</span>
                    <button onClick={onDismissError} style={{ background: "none", border: "none", padding: 0 }}><X size={12} color={T.red} /></button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const FreshStoreCheckout: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const items = useSelector((s: RootState) => (s.cart.items ?? []) as CartLine[]);
  const listings = useSelector((s: RootState) => ((s as any).food?.listings ?? []) as any[]);
  const user = useSelector((s: RootState) => s.auth.user);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const isLoggedIn = !!user;

  /* ── service fee — food earnings slice ── */
  const serviceFeeState = useSelector((s: RootState) => (s as any).foodServiceFee) ?? {
  items: [], guestFee: 0, smallOrderFee: 0, totalCharged: 0,
};
const {
  guestFee: feeGuestsFee,
  smallOrderFee: feeSmallOrderFee,
  totalCharged: feeTotalCharged,
} = serviceFeeState;
// no subtotal/loading/error/calculated fields exist on this slice —
// derive subtotal locally and treat "loading" as always-false (sync reducers)
const feeLoading = false;
const feeError: string | null = null;
const earningsUpdated = serviceFeeState.items.length > 0;

  const getListingById = useCallback(
    (id: string) => listings.find((l: any) => l.listingId === id),
    [listings]
  );

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);
  const brandName = useCallback(
    (id: string) => brandMap.get(id)?.name || `Grower ${id.slice(0, 6)}`,
    [brandMap]
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => { const fn = () => setIsMobile(window.innerWidth <= 768); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);

  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setUserCoords(null),
    );
  }, []);

  const [step, setStep] = useState<StepKey>("basket");
  const stepIdx = Math.max(0, ACTIVE_STEPS.indexOf(step));
  const nextStep = (from: StepKey): StepKey => ACTIVE_STEPS[ACTIVE_STEPS.indexOf(from) + 1] ?? "payment";

  /* ── keep the food feed live so stock, dates and photos resolve ──
     One published-feed listener rather than a per-item one: the food
     slice's single-item listener writes to currentFoodItem, so calling
     it in a loop would just overwrite itself. */
  useEffect(() => { dispatch(listenToPublishedFoods() as any); }, [dispatch]);

  /* ── split the basket into buyable vs not ── */
  const goneItems = useMemo(
    () => items.filter((i) => isUnavailable(i, getListingById(realIdOf(i)))),
    [items, getListingById]
  );
  const availableItems = useMemo(
    () => items.filter((i) => !isUnavailable(i, getListingById(realIdOf(i)))),
    [items, getListingById]
  );

  /* ── checked lines (only among available) ── */
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const seenKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fresh = items.map((i) => i.listingId).filter((k) => !seenKeys.current.has(k));
    if (!fresh.length) return;
    fresh.forEach((k) => seenKeys.current.add(k));
    setCheckedItems((prev) => new Set([...prev, ...fresh]));
  }, [items]);

  // Never keep an unavailable line checked.
  useEffect(() => {
    if (!goneItems.length) return;
    setCheckedItems(prev => {
      const next = new Set(prev);
      goneItems.forEach((i) => next.delete(i.listingId));
      return next;
    });
  }, [goneItems]);

  const checkedItemsList = useMemo(
    () => availableItems.filter((i) => checkedItems.has(i.listingId)),
    [availableItems, checkedItems]
  );

  const checkedBrandIds = useMemo<string[]>(
    () => Array.from(new Set(checkedItemsList.map((i) => (i.brandId ?? i.hostId) as string).filter(Boolean))),
    [checkedItemsList]
  );

  /* ── "Myself" vs "Gift" ── */
  const [useMyself, setUseMyself] = useState<boolean>(isLoggedIn);
  const [giftSomeone, setGiftSomeone] = useState(false);

  const [myself, setMyself] = useState<AddrState>(emptyAddr());
  const patchMyself = useCallback((p: Partial<AddrState>) => setMyself(prev => ({ ...prev, ...p })), []);

  const [gift, setGift] = useState<RecipientState>(emptyAddr());
  const patchGift = useCallback((p: Partial<RecipientState>) => setGift(prev => ({ ...prev, ...p })), []);

  // Populate ONLY what the profile already has; leave the rest for the user.
  useEffect(() => {
    if (!user) return;
    const ship = user.shippingAddress;
    const email = user.contactEmail || user.email || (user as any).emailAddress || "";
    const rawPhone = user.contactNumber || (user as any).whatsappNumber || "";
    const dialMatch = rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode = dialMatch ? dialMatch[1] : (ship?.countryCode ? `+${ship.countryCode}` : "+254");
    const phone = dialMatch ? rawPhone.replace(dialMatch[0], "") : rawPhone;
    setMyself({
      firstName: user.firstName || "",
      lastName: user.lastName || (user as any).surName || "",
      email,
      country: ship?.country || "",
      state: ship?.state || "",
      city: ship?.city || "",
      postalCode: ship?.zipCode || "",
      address: ship?.streetAddress || "",
      dialCode,
      phone,
    });
  }, [user]);

  const missing = useMemo(() => ({
    firstName: !myself.firstName?.trim(),
    email: !myself.email?.trim(),
    city: !myself.city?.trim(),
    address: !myself.address?.trim(),
    phone: !myself.phone?.trim(),
  }), [myself]);

  const toggleMyself = useCallback(() => setUseMyself(v => { const nv = !v; if (nv) setGiftSomeone(false); return nv; }), []);
  const toggleGift = useCallback(() => setGiftSomeone(v => { const nv = !v; if (nv) setUseMyself(false); return nv; }), []);

  /* ── per-grower delivery rates via nearest zone ── */
  const [brandRates, setBrandRates] = useState<Record<string, any[]>>({});
  const [brandLoading, setBrandLoading] = useState<Record<string, boolean>>({});
  const [brandErrors, setBrandErrors] = useState<Record<string, string | null>>({});
  const [brandZones, setBrandZones] = useState<Record<string, any>>({});
  const [selectedBrandRates, setSelectedBrandRates] = useState<Record<string, any>>({});

  const fetchBrandRates = useCallback(async () => {
    if (!userCoords) return;
    for (const bid of checkedBrandIds) {
      setBrandLoading(p => ({ ...p, [bid]: true }));
      setBrandErrors(p => ({ ...p, [bid]: null }));
      try {
        const result = await dispatch(nearestDeliveryLocation({
          brandId: bid, userLatitude: userCoords.lat, userLongitude: userCoords.lng,
        })).unwrap();
        const zone = result.matchedZone
          ? { name: result.matchedZone.name || result.matchedZone.type, type: result.matchedZone.type }
          : null;
        const rate = result.matchedZone ? {
          productCode: "FBS",
          productName: result.matchedZone.type === "local" ? "Local delivery"
            : result.matchedZone.type === "regional" ? "Regional delivery" : "International delivery",
          totalPrice: result.deliveryFee,
          deliveryTime: `${result.matchedZone.deliveryTime ?? 1}–${(result.matchedZone.deliveryTime ?? 1) + 1} days`,
        } : null;
        setBrandZones(p => ({ ...p, [bid]: zone }));
        setBrandRates(p => ({ ...p, [bid]: rate ? [rate] : [] }));
        if (rate) setSelectedBrandRates(p => ({ ...p, [bid]: rate }));
      } catch {
        setBrandErrors(p => ({ ...p, [bid]: "Couldn't get a rate from this grower." }));
        setBrandRates(p => ({ ...p, [bid]: [] }));
      }
      setBrandLoading(p => ({ ...p, [bid]: false }));
    }
  }, [userCoords, checkedBrandIds, dispatch]);

  const onSelectBrandRate = useCallback((brandId: string, rate: any) => setSelectedBrandRates(p => ({ ...p, [brandId]: rate })), []);

  /* ── totals ──
     Each line's rate already carries its size adjustment and extras, so
     rawSubtotal is the whole item cost. Delivery is never folded into
     the earnings call — it's added client-side once a real quote lands. */
  const rawSubtotal = useMemo(() => checkedItemsList.reduce((s, i) => s + linePrice(i) * (i.qty ?? 1), 0), [checkedItemsList]);
  const totalQty = useMemo(() => checkedItemsList.reduce((s, i) => s + (i.qty ?? 1), 0), [checkedItemsList]);
  const deliveryFee = useMemo(
    () => checkedBrandIds.reduce((sum, bid) => { const r = selectedBrandRates[bid]; return sum + (r ? (r.totalPrice ?? r.price ?? 0) : 0); }, 0),
    [checkedBrandIds, selectedBrandRates]
  );

  const subtotal = rawSubtotal; 
  const guestsFee = earningsUpdated ? feeGuestsFee : 0;
  const smallOrderFee = feeSmallOrderFee ?? 0;
  const grandTotal = (earningsUpdated ? feeTotalCharged : rawSubtotal) + deliveryFee + smallOrderFee;

  useEffect(() => {
  dispatch(clearOrder());
  if (!checkedItemsList.length) return;

  checkedItemsList.forEach((i) => {
    dispatch(addItem({
      listingId: realIdOf(i),
      name: i.name,
      rate: linePrice(i),
      rateType: (i.rateType ?? getListingById(realIdOf(i))?.rateType ?? "perItem") as RateType,
      quantity: i.qty,
    }));
    // find() relies on the item we just pushed being the last match by listingId
    const itemId = /* capture id from a ref map, see note below */ undefined;
  });

  dispatch(setGuestFee(calculateGuestServiceFee(rawSubtotal)));
  dispatch(setSmallOrderFee(calculateSmallOrderFee(rawSubtotal)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [checkedItemsList, rawSubtotal, dispatch]);

  useEffect(() => { if (!items.length) navigate(`/FreshStoreHome/${userId ?? ""}`); }, [items, navigate, userId]);

  useEffect(() => {
    if (step !== "payment" || !userCoords) return;
    if (checkedBrandIds.length > 0 && Object.keys(selectedBrandRates).length === 0) fetchBrandRates();
  }, [step, userCoords, checkedBrandIds.join(",")]); // eslint-disable-line

  const [payingCard, setPayingCard] = useState(false);
  const [payingMpesa, setPayingMpesa] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("card");
  const [savingProfile, setSavingProfile] = useState(false);
  const paying = payingCard || payingMpesa;
  const canPay = !paying && checkedItemsList.length > 0 && step === "payment";

  const effectiveAddr: AddrState = giftSomeone ? gift : myself;

  /* ── account resolution: only a gift creates a new account ── */
  const resolvedAccountRef = useRef<{ key: string; userId: string } | null>(null);

  const ensureCustomerAccount = async (): Promise<string> => {
    if (!giftSomeone && isLoggedIn) return user!.userId;

    const source = giftSomeone ? gift : myself;
    const cacheKey = `${giftSomeone ? "gift" : "self"}:${source.email.trim().toLowerCase()}`;
    if (resolvedAccountRef.current?.key === cacheKey) return resolvedAccountRef.current.userId;

    const res = await axios.post<CreateCustomerResponse>(CREATE_CUSTOMER_URL, {
      firstName: source.firstName, lastName: source.lastName, email: source.email,
      phone: `${source.dialCode}${source.phone}`,
      shippingAddress: {
        streetAddress: source.address, city: source.city, state: source.state,
        country: source.country, zipCode: source.postalCode,
      },
      buyForSomeoneElse: giftSomeone,
      buyerUserId: isLoggedIn ? user!.userId : null,
    });
    if (!res.data?.userId) throw new Error("We couldn't set up the recipient's account. Check their email and try again.");
    resolvedAccountRef.current = { key: cacheKey, userId: res.data.userId };
    return res.data.userId;
  };

  /* ── Continue from delivery: write the profile, then advance ── */
  const handleContinueDelivery = useCallback(async () => {
    setPayError(null);
    if (useMyself && isLoggedIn) {
      setSavingProfile(true);
      try {
        await dispatch(updateUserProfile({
          firstName: myself.firstName,
          lastName: myself.lastName,
          contactEmail: myself.email,
          contactNumber: `${myself.dialCode}${myself.phone}`,
          shippingAddress: {
            streetAddress: myself.address,
            city: myself.city,
            state: myself.state,
            country: myself.country,
            zipCode: myself.postalCode,
          },
        } as any)).unwrap();
      } catch (e: any) {
        setSavingProfile(false);
        setPayError(e?.message || "Couldn't save your details. Please try again.");
        return;
      }
      setSavingProfile(false);
    }
    setStep(nextStep("delivery"));
    fetchBrandRates();
  }, [useMyself, isLoggedIn, myself, dispatch, fetchBrandRates]);

  /* ── payload ──
     Refs carry the REAL listing id plus the size and extras, so the
     backend can re-price each line itself. The composite cart key is
     not a real listing id and can't be looked up. Totals stay
     display-only; the backend re-derives them. */
  const buildPayload = useCallback(async () => {
    const resolvedUserId = await ensureCustomerAccount();
    const itemRefs: string[] = checkedItemsList.map((item) => {
      const realId = realIdOf(item);
      /* every portion picked, primary first — the size that set the price plus any extra helpings */
      const sizes = (item.portionIds?.length ? item.portionIds : (item.portionId ? [item.portionId] : [])).join("+");
      const extras = (item.extraIds || []).join(",");
      return `${realId}:${item.hostId}:${item.qty}:${linePrice(item)}:${sizes}:${extras}`;
    });
    return {
      resolvedUserId,
      body: {
        userId: resolvedUserId,
        itemRefs,
        items: checkedItemsList.map((i) => ({
          listingId: realIdOf(i), cartLineId: i.listingId,
          brandId: i.brandId, hostId: i.hostId,
          name: i.name, rate: i.rate ?? i.price ?? 0, unitPrice: linePrice(i), qty: i.qty, photo: i.photo,
          rateType: (i.rateType ?? getListingById(realIdOf(i))?.rateType) ?? "perItem",
          lineTotal: Number((linePrice(i) * (i.qty ?? 1)).toFixed(2)),
          portionId: i.portionId, portionIds: i.portionIds, extraIds: i.extraIds, extras: i.extras,
          unit: i.unit, netContent: i.netContent,
        })),
        userLatitude: userCoords?.lat ?? null,
        userLongitude: userCoords?.lng ?? null,
        deliveredBy: selectedBrandRates,
        deliveryFee,
        fulfillmentMethod: "FBS",
        buyerUserId: user?.userId ?? null,
        giftRecipientUserId: giftSomeone ? resolvedUserId : null,
      },
    };
  }, [checkedItemsList, selectedBrandRates, deliveryFee, giftSomeone, gift, effectiveAddr, userCoords, user]);

  const redirectFromResponse = (data: any) => {
    const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    const sessionId = data?.id;
    if (directUrl) { window.location.href = directUrl; return; }
    if (sessionId) { window.location.href = `https://checkout.stripe.com/c/pay/${sessionId}`; return; }
    throw new Error("The checkout didn't return a payment link. Please try again.");
  };

  const handlePayCard = useCallback(async () => {
    if (!canPay) return;
    setPayingCard(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post(FRESH_CHECKOUT_URL, body);
      redirectFromResponse(res.data);
    } catch (e: any) {
      setPayError(e?.response?.data?.error ?? e?.message ?? "Card payment didn't go through. Please try again.");
      setPayingCard(false);
    }
  }, [canPay, buildPayload]);

  const handlePayMpesa = useCallback(async () => {
    if (!canPay) return;
    setPayingMpesa(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post(FRESH_MPESA_CHECKOUT_URL, body);
      const directUrl = (res.data as any)?.url ?? (res.data as any)?.data?.url ?? (res.data as any)?.sessionUrl ?? (res.data as any)?.checkoutUrl;
      if (!directUrl) throw new Error("M-Pesa didn't return a payment link. Please try again.");
      window.location.href = directUrl;
    } catch (e: any) {
      setPayError(e?.response?.data?.error ?? e?.message ?? "M-Pesa payment didn't go through. Please try again.");
      setPayingMpesa(false);
    }
  }, [canPay, buildPayload]);

  /* ── basket actions ──
     Increment re-dispatches the exact same composite line so it bumps the
     existing row instead of creating a base-only one. */
  const handleInc = useCallback((item: CartLine) => {
    const listing = getListingById(realIdOf(item));
    const stock = stockOf(item, listing);
    if (stock != null && item.qty >= stock) return;
    dispatch(addToCart({
      listingId: item.listingId,
      baseListingId: item.baseListingId,
      name: item.name,
      rate: linePrice(item),
      rateType: item.rateType,
      unitPrice: item.unitPrice,
      extras: item.extras,
      photo: item.photo,
      hostId: item.hostId,
      brandId: item.brandId,
      unit: item.unit,
      netContent: item.netContent,
      portionId: item.portionId,
      portionIds: item.portionIds,
      extraIds: item.extraIds,
    } as any));
  }, [dispatch, getListingById]);

  const handleDec = useCallback((item: CartLine) => dispatch(decrementCartItem({ listingId: item.listingId })), [dispatch]);
  const handleRemove = useCallback((item: CartLine) => dispatch(removeFromCart({ listingId: item.listingId })), [dispatch]);
  const handleNav = useCallback((item: CartLine) => {
    const bid = item.brandId ?? item.hostId;
    navigate(bid ? `/FreshStore/${bid}` : `/FreshStoreHome/${userId ?? ""}`);
  }, [navigate, userId]);
  const handleSimilar = useCallback(() => navigate(`/FreshStoreHome/${userId ?? ""}`), [navigate, userId]);

  const toggleItem = useCallback((key: string) => setCheckedItems(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }), []);
  const toggleAll = useCallback(() => {
    if (availableItems.every((i) => checkedItems.has(i.listingId))) setCheckedItems(new Set());
    else setCheckedItems(new Set(availableItems.map((i) => i.listingId)));
  }, [availableItems, checkedItems]);

  const sym = "€";
  const deliveryReady = useMyself
    ? !!(myself.firstName && myself.phone)
    : giftSomeone
      ? !!(gift.firstName && gift.email)
      : false;

  return (
    <div className="fsc-scope">
      <style>{CSS}</style>

      {/* header */}
      <header style={{ position: "sticky", top: 0, zIndex: 300, background: "rgba(255,255,255,.96)", backdropFilter: "blur(10px)", borderBottom: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(27,14,6,.05)" }}>
        <div style={{ height: 3, background: GRAD_H, backgroundSize: "200% 100%", animation: "fscGrad 5s ease infinite" }} />
        <div style={{ display: "flex", alignItems: "center", padding: "0 20px", height: 54, gap: 14 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: T.muted, display: "flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
            <Sparkles size={14} color={T.gold} />
            <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontWeight: 600, color: T.ink }}>Checkout</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ShoppingBag size={16} color={T.red} />
            <span style={{ fontSize: 12, color: T.ink, fontWeight: 800 }}>{totalQty}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "7px 0 9px", borderTop: `1px solid ${T.divider}` }}>
          {ACTIVE_STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`fsc-bc${s === step ? " on" : ""}`}>{s === "basket" ? "Basket" : s === "delivery" ? "Delivery" : "Payment"}</span>
              {i < ACTIVE_STEPS.length - 1 && <span className="fsc-bc-sep">›</span>}
            </React.Fragment>
          ))}
        </div>
      </header>

      {/* body */}
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: isMobile ? "16px 14px 130px" : "28px 40px 60px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 330px", gap: 26, alignItems: "start" }}>
        <div style={{ minWidth: 0 }}>
          <BasketStep
            items={availableItems} goneItems={goneItems} getListing={getListingById}
            stepIdx={stepIdx} stepNumber={1} sym={sym}
            checkedItems={checkedItems} onToggleAll={toggleAll} onToggleItem={toggleItem}
            onContinue={() => setStep(nextStep("basket"))}
            onInc={handleInc} onDec={handleDec} onRemove={handleRemove} onNavigate={handleNav}
            onSimilar={handleSimilar}
          />

          {stepIdx >= 1 && (
            <DeliveryStep
              stepIdx={stepIdx} myIdx={1} stepNumber={2}
              useMyself={useMyself} onToggleMyself={toggleMyself}
              myself={myself} onPatchMyself={patchMyself} missing={missing}
              giftSomeone={giftSomeone} onToggleGift={toggleGift} gift={gift} onPatchGift={patchGift}
              onContinue={handleContinueDelivery} onBack={() => setStep("delivery")} saving={savingProfile}
            />
          )}

          {stepIdx >= 2 && (
            <PaymentStep
              stepIdx={stepIdx} myIdx={2} stepNumber={3} addr={effectiveAddr}
              subtotal={subtotal} guestsFee={guestsFee} smallOrderFee={smallOrderFee} sym={sym} grandTotal={grandTotal} feeLoading={feeLoading}
              canPay={canPay} payingCard={payingCard} payingMpesa={payingMpesa} payError={payError}
              onPayCard={handlePayCard} onPayMpesa={handlePayMpesa} onDismissError={() => setPayError(null)}
              selectedPayment={selectedPayment} onSelectPayment={setSelectedPayment}
              brandRates={brandRates} brandLoading={brandLoading} brandErrors={brandErrors} brandZones={brandZones}
              selectedBrandRates={selectedBrandRates} onSelectBrandRate={onSelectBrandRate} refetchRates={fetchBrandRates}
              checkedBrandIds={checkedBrandIds} brandName={brandName}
            />
          )}
        </div>

        {/* order summary */}
        <div style={{ position: isMobile ? "static" : "sticky", top: 118, minWidth: 0 }}>
          <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 18, padding: "18px 16px", marginBottom: 12, boxShadow: "0 3px 12px rgba(27,14,6,.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
              <div style={{ width: 24, height: 24, borderRadius: 8, background: GRAD_H, display: "flex", alignItems: "center", justifyContent: "center" }}><ShoppingBag size={12} color="#fff" /></div>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".18em", color: T.muted }}>ORDER SUMMARY</span>
            </div>
            <FeeRow label="Subtotal" value={fmtPrice(subtotal, sym)} sub={`${totalQty} item${totalQty !== 1 ? "s" : ""} · sizes & extras included`} />
            <FeeRow label="Service fee" value={fmtPrice(guestsFee, sym)} sub="Keeps the lights on" muted loading={feeLoading} />
            {smallOrderFee > 0 && (
              <FeeRow label="Small order fee" value={fmtPrice(smallOrderFee, sym)} sub="Applies to orders under €150" muted />
            )}
            <FeeRow label="Delivery"
              value={checkedBrandIds.some(bid => selectedBrandRates[bid]) ? fmtPrice(deliveryFee, sym) : "Chosen next"}
              loading={Object.values(brandLoading).some(Boolean)} />
            {giftSomeone && <FeeRow label="Gift" value="Delivers to recipient" green />}
            {goneItems.length > 0 && <FeeRow label="Unavailable" value={`${goneItems.length} can't be shipped`} red />}
            <div style={{ paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".13em", color: T.ink }}>TOTAL</span>
              <span style={{ fontSize: 23, fontWeight: 900, color: T.red }}>{fmtPrice(grandTotal, sym)}</span>
            </div>
            <div style={{ fontSize: 10, color: T.muted, textAlign: "right", marginTop: 4 }}>Taxes included · charged in EUR</div>
            {feeError && <div style={{ fontSize: 10, color: T.red, marginTop: 8 }}>{feeError}</div>}
          </div>
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", background: T.panel2, borderRadius: 14, border: `1px solid ${T.border}` }}>
              <Lock size={13} color={T.green} />
              <span style={{ fontSize: 11, color: T.muted }}>Payments are encrypted end to end.</span>
            </div>
          )}
        </div>
      </div>

      {/* mobile sticky CTA */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,0.97)", borderTop: `1px solid ${T.border}`, padding: "12px 16px", zIndex: 200, backdropFilter: "blur(10px)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.muted }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>{fmtPrice(grandTotal, sym)}</span>
          </div>
          {step === "basket" && <button onClick={() => setStep(nextStep("basket"))} className={`fsc-cta ${checkedItemsList.length ? "go" : "wait"}`} disabled={!checkedItemsList.length}>Continue <ChevronRight size={14} /></button>}
          {step === "delivery" && <button onClick={handleContinueDelivery} className={`fsc-cta ${deliveryReady && !savingProfile ? "go" : "wait"}`} disabled={!deliveryReady || savingProfile}>{savingProfile ? "Saving…" : <>Continue <ChevronRight size={14} /></>}</button>}
          {step === "payment" && (
            selectedPayment === "card" ? (
              <button onClick={handlePayCard} className={`fsc-cta ${canPay ? "go" : "wait"}`} disabled={!canPay}>
                {payingCard ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "fscSpin 1s linear infinite" }} /> : <Lock size={12} />}
                {payingCard ? "Processing…" : `Pay ${fmtPrice(grandTotal, sym)}`}
              </button>
            ) : (
              <button onClick={handlePayMpesa} className={`fsc-cta ${canPay ? "mpesa" : "wait"}`} disabled={!canPay}>
                {payingMpesa ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "fscSpin 1s linear infinite" }} /> : <Smartphone size={12} />}
                {payingMpesa ? "Connecting…" : `M-Pesa · ${fmtPrice(grandTotal, sym)}`}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default FreshStoreCheckout;
